import { BookOpen, FileCheck, Wallet, CreditCard, Shield, Lock, TrendingUp, BarChart3, CheckCircle, Clock } from 'lucide-react';

export default function HomePage() {
  const features = [
    {
      icon: BookOpen,
      title: 'Ledger',
      description: 'Maintain comprehensive financial records with detailed transaction tracking and reporting.',
      color: 'from-blue-600 to-blue-700',
    },
    {
      icon: FileCheck,
      title: 'Auditing',
      description: 'Ensure transparency and accountability with built-in audit trails and compliance tools.',
      color: 'from-indigo-600 to-indigo-700',
    },
    {
      icon: Wallet,
      title: 'Account Management',
      description: 'Manage multiple accounts and track balances with ease and precision.',
      color: 'from-blue-700 to-blue-800',
    },
    {
      icon: CreditCard,
      title: 'Subscription Collections',
      description: 'Streamline membership dues and subscription payments with automated tracking.',
      color: 'from-indigo-700 to-indigo-800',
    },
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Bank-Grade Security',
      description: 'Enterprise-level encryption and security protocols protect your financial data.',
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Analytics',
      description: 'Access up-to-date financial insights and performance metrics instantly.',
    },
    {
      icon: BarChart3,
      title: 'Comprehensive Reports',
      description: 'Generate detailed financial reports for meetings and audits with one click.',
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Navigation Bar */}
      <nav className="border-b border-slate-200 bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700">
                <Wallet className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">Treasurer</div>
                <div className="text-xs text-slate-500">Financial Management</div>
              </div>
            </div>
            <a 
              href="https://masonic-tools.co.uk" 
              className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
            >
              PART OF MASONIC TOOLS
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 px-4 py-24 text-white">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-blue-500 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 h-96 w-96 rounded-full bg-indigo-500 blur-3xl"></div>
        </div>
        
        <div className="container relative mx-auto">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-flex items-center rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-2 backdrop-blur-sm">
              <Lock className="mr-2 h-4 w-4" />
              <span className="text-sm font-medium">Secure • Reliable • Professional</span>
            </div>
            
            <h1 className="mb-6 text-6xl font-bold leading-tight tracking-tight md:text-7xl">
              Treasurer
            </h1>
            
            <p className="mb-8 text-xl leading-relaxed text-blue-100 md:text-2xl">
              Professional financial management platform designed specifically for unit treasurers. 
              Maintain accurate records, ensure compliance, and streamline financial operations.
            </p>
            
            <div className="mb-12 flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center rounded-lg bg-white/10 px-4 py-2 backdrop-blur-sm">
                <CheckCircle className="mr-2 h-5 w-5 text-green-400" />
                <span className="text-sm font-medium">Audit Ready</span>
              </div>
              <div className="flex items-center rounded-lg bg-white/10 px-4 py-2 backdrop-blur-sm">
                <CheckCircle className="mr-2 h-5 w-5 text-green-400" />
                <span className="text-sm font-medium">Automated Tracking</span>
              </div>
              <div className="flex items-center rounded-lg bg-white/10 px-4 py-2 backdrop-blur-sm">
                <CheckCircle className="mr-2 h-5 w-5 text-green-400" />
                <span className="text-sm font-medium">Complete Transparency</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-20">
        <div className="container mx-auto">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold text-slate-900">Core Financial Tools</h2>
            <p className="text-lg text-slate-600">
              Everything you need to manage your unit finances with confidence
            </p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-2">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-lg transition-all duration-300 hover:shadow-2xl"
                >
                  {/* Gradient background on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 transition-opacity duration-300 group-hover:opacity-5`}></div>
                  
                  <div className="relative">
                    <div className={`mb-6 inline-flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} shadow-lg`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="mb-3 text-2xl font-bold text-slate-900">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-slate-100 px-4 py-20">
        <div className="container mx-auto">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold text-slate-900">Built for Trust & Transparency</h2>
            <p className="text-lg text-slate-600">
              Professional-grade features that instill confidence
            </p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-3">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="rounded-xl bg-white p-8 shadow-md transition-all duration-300 hover:shadow-xl"
                >
                  <div className="mb-6">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-slate-700 to-slate-900">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-slate-900">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Status Section */}
      <section className="px-4 py-20">
        <div className="container mx-auto">
          <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="bg-gradient-to-br from-slate-50 to-white p-12">
              <div className="mb-8 text-center">
                <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg">
                  <Clock className="h-10 w-10 text-white" />
                </div>
                <div className="mb-4 inline-block rounded-full bg-amber-50 px-6 py-2">
                  <span className="text-sm font-semibold uppercase tracking-wider text-amber-800">
                    Launching Soon
                  </span>
                </div>
                <h3 className="mb-4 text-3xl font-bold text-slate-900">
                  Preparing for Launch
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  We are putting the finishing touches on the Treasurer application to ensure 
                  it meets the highest standards of security, reliability, and usability.
                </p>
              </div>
              
              <div className="rounded-xl border border-slate-200 bg-white p-6">
                <h4 className="mb-4 text-center text-sm font-semibold uppercase tracking-wider text-slate-500">
                  Coming Soon
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex items-center rounded-lg bg-slate-50 px-4 py-3">
                    <CheckCircle className="mr-3 h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-slate-700">Advanced Ledger System</span>
                  </div>
                  <div className="flex items-center rounded-lg bg-slate-50 px-4 py-3">
                    <CheckCircle className="mr-3 h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-slate-700">Audit Trail Reporting</span>
                  </div>
                  <div className="flex items-center rounded-lg bg-slate-50 px-4 py-3">
                    <CheckCircle className="mr-3 h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-slate-700">Multi-Account Support</span>
                  </div>
                  <div className="flex items-center rounded-lg bg-slate-50 px-4 py-3">
                    <CheckCircle className="mr-3 h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-slate-700">Subscription Management</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-900 px-4 py-12 text-white">
        <div className="container mx-auto">
          <div className="mb-8 flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700">
                <Wallet className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold">Treasurer</div>
                <div className="text-xs text-slate-400">by Masonic Tools</div>
              </div>
            </div>
            <div className="flex items-center gap-8 text-sm text-slate-400">
              <a href="https://masonic-tools.co.uk" className="transition-colors hover:text-white">
                Home
              </a>
              <span>•</span>
              <span>Secure Platform</span>
              <span>•</span>
              <span>Professional Grade</span>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            © {new Date().getFullYear()} Masonic Tools. All rights reserved. • Secure • Trusted • Professional
          </div>
        </div>
      </footer>
    </div>
  );
}

